import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
import { completeTask, decideMilestone, getProject, type PortalProjectDetail } from '@mkcore/portal/schema';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Skeleton, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { healthTone } from '@/features/portal/projects';

/**
 * A project of the client (CO-0661, CO-0666): the milestones, which the client accepts or turns down with why once
 * delivered, the tasks for the client, which it finishes with a comment, and the weekly statuses MKC shared.
 */
export const Route = createFileRoute('/portal/_client/projects/$id')({
  component: Project,
});

type Detail = z.output<typeof PortalProjectDetail>;
type Milestone = Detail['milestones'][number];
type Task = Detail['tasks'][number];

function useApply(id: string) {
  const qc = useQueryClient();
  return async (after: Detail) => {
    qc.setQueryData(['portal', 'project', id], after);
    await qc.invalidateQueries({ queryKey: ['portal', 'projects'] });
    await qc.invalidateQueries({ queryKey: ['portal', 'home'] });
  };
}

function Project() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const q = useQuery({ queryKey: ['portal', 'project', id], queryFn: () => call(getProject, { id }) });
  if (q.isError) return <ErrorState title={t('portal.projects.notFound')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  const p = q.data;
  const open = p.tasks.filter((x) => !x.done);
  const done = p.tasks.filter((x) => x.done);
  return (
    <>
      <Link to="/portal/projects" className="flex w-fit items-center gap-1 py-1 text-sm text-fg-2 hover:underline">
        <ArrowLeft className="size-4" aria-hidden /> {t('portal.modules.projects')}
      </Link>
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">{p.name}</h1>
          {p.health && <Badge tone={healthTone[p.health]}>{t(`portal.projects.health.${p.health}`)}</Badge>}
          {p.status !== 'active' && <Badge tone="neutral">{t(`portal.projects.status.${p.status}`, { defaultValue: p.status })}</Badge>}
        </div>
        {(p.startsOn || p.endsOn) && (
          <p className="text-sm text-fg-2">{[p.startsOn && formatDate(p.startsOn), p.endsOn && formatDate(p.endsOn)].filter(Boolean).join(' – ')}</p>
        )}
      </div>
      <Card className="grid gap-3 p-4">
        <h2 className="text-lg font-bold">{t('portal.projects.milestones')}</h2>
        {!p.milestones.length ? (
          <p className="text-sm text-fg-3">{t('portal.projects.noMilestones')}</p>
        ) : (
          <ul className="grid gap-3">
            {p.milestones.map((m) => (
              <MilestoneItem key={m.id} projectId={p.id} m={m} />
            ))}
          </ul>
        )}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-lg font-bold">{t('portal.projects.tasks')}</h2>
        {!p.tasks.length ? (
          <EmptyState title={t('portal.projects.noTasks')} />
        ) : (
          <ul className="grid gap-3">
            {[...open, ...done].map((x) => (
              <TaskItem key={x.id} projectId={p.id} task={x} />
            ))}
          </ul>
        )}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-lg font-bold">{t('portal.projects.updates')}</h2>
        {!p.updates.length ? (
          <p className="text-sm text-fg-3">{t('portal.projects.noUpdates')}</p>
        ) : (
          <ol className="grid gap-3">
            {p.updates.map((u) => (
              <li key={u.week} className="grid gap-1 border-l-2 border-line pl-3">
                <span className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-bold">{t('portal.projects.weekOf', { date: formatDate(u.week) })}</span>
                  {u.health in healthTone && <Badge tone={healthTone[u.health as keyof typeof healthTone]}>{t(`portal.projects.health.${u.health}`)}</Badge>}
                </span>
                <p className="whitespace-pre-wrap text-sm text-fg-2">{u.summary}</p>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  );
}

function MilestoneItem({ projectId, m }: { projectId: string; m: Milestone }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const apply = useApply(projectId);
  const [mode, setMode] = useState<'accept' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const decide = async (decision: 'accept' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      await apply(await call(decideMilestone, { id: m.id, decision, ...(comment.trim() && { comment: comment.trim() }) }));
      setMode(null);
      setComment('');
    } catch (err) {
      setError(message(err));
    }
    setBusy(false);
  };
  const titleId = `milestone-${m.id}`;
  return (
    <li className="grid gap-2 border-b border-line-subtle pb-3 last:border-0" aria-labelledby={titleId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span id={titleId} className="font-bold">
          {m.title}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-sm">
          {m.dueOn && <span className="text-fg-2">{formatDate(m.dueOn)}</span>}
          <Badge tone={m.accepted ? 'success' : m.canDecide ? 'info' : 'neutral'}>{t(m.accepted ? 'portal.projects.milestone.accepted' : m.canDecide ? 'portal.projects.milestone.delivered' : `portal.projects.milestone.${m.status}`)}</Badge>
        </span>
      </div>
      {m.description && <p className="whitespace-pre-wrap text-sm text-fg-2">{m.description}</p>}
      {m.accepted && (
        <p role="status" className="text-sm text-fg-2">
          {t('portal.projects.acceptedBy', { name: m.accepted.byName ?? '', at: formatDateTime(m.accepted.at) })}
        </p>
      )}
      {m.rejected && (
        <p className="text-sm text-fg-2">
          {t('portal.projects.rejectedBy', { name: m.rejected.byName ?? '', at: formatDateTime(m.rejected.at) })} <span className="italic">{m.rejected.note}</span>
        </p>
      )}
      {m.canDecide &&
        (mode === null ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setMode('accept')}>
              {t('portal.projects.accept')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMode('reject')}>
              {t('portal.projects.reject')}
            </Button>
          </div>
        ) : (
          <div className="grid gap-2 rounded-md border border-line p-3">
            <p className="text-sm">{t(mode === 'accept' ? 'portal.projects.acceptHint' : 'portal.projects.rejectHint')}</p>
            <Field label={t(mode === 'accept' ? 'portal.projects.comment' : 'portal.projects.whatMissing')}>
              {(f) => <Textarea {...f} rows={3} value={comment} autoFocus onChange={(e) => setComment(e.target.value)} />}
            </Field>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={mode === 'accept' ? 'primary' : 'secondary'} loading={busy} disabled={mode === 'reject' && !comment.trim()} onClick={() => void decide(mode)}>
                {t(mode === 'accept' ? 'portal.projects.confirmAccept' : 'portal.projects.confirmReject')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ))}
    </li>
  );
}

function TaskItem({ projectId, task }: { projectId: string; task: Task }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const apply = useApply(projectId);
  const [finishing, setFinishing] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      await apply(await call(completeTask, { id: task.id, ...(comment.trim() && { comment: comment.trim() }) }));
      setFinishing(false);
    } catch (err) {
      setError(message(err));
    }
    setBusy(false);
  };
  return (
    <li className="grid gap-2 border-b border-line-subtle pb-3 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={task.done ? 'text-fg-3' : 'font-bold'}>
          <span className="tabular text-fg-3">{task.key}</span> {task.title}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-sm">
          {task.dueOn && !task.done && <span className="text-fg-2">{t('portal.projects.dueOn', { date: formatDate(task.dueOn) })}</span>}
          <Badge tone={task.done ? 'success' : 'info'}>{t(task.done ? 'portal.projects.taskDone' : 'portal.projects.taskOpen')}</Badge>
        </span>
      </div>
      {task.description && !task.done && <p className="whitespace-pre-wrap text-sm text-fg-2">{task.description}</p>}
      {task.done && (task.doneByName || task.clientNote) && (
        <p className="text-sm text-fg-2">
          {task.doneByName && t('portal.projects.doneBy', { name: task.doneByName, at: task.doneAt ? formatDateTime(task.doneAt) : '' })} {task.clientNote && <span className="italic">{task.clientNote}</span>}
        </p>
      )}
      {!task.done &&
        (!finishing ? (
          <div>
            <Button size="sm" variant="secondary" onClick={() => setFinishing(true)}>
              {t('portal.projects.finish')}
            </Button>
          </div>
        ) : (
          <div className="grid gap-2 rounded-md border border-line p-3">
            <Field label={t('portal.projects.comment')}>{(f) => <Textarea {...f} rows={3} value={comment} autoFocus onChange={(e) => setComment(e.target.value)} />}</Field>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" loading={busy} onClick={() => void finish()}>
                {t('portal.projects.confirmFinish')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setFinishing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ))}
    </li>
  );
}
